import { devError } from './developmentUtils';

/**
 * When the number in data array is bigger than 255 it is ignored.
 * That way a long sequence can be easily verified even if it contains
 * byte inside that are randomly changed (like CRC checks or size of files ...)
 */
export interface IDataSignature {
    readonly offset: number;
    readonly data: Array<number>;
}

export interface IFileSignature {
    readonly extension: string;
    readonly description: string;
    readonly mimeType: string;
    readonly signatures: Array<IDataSignature>;
}

/**
 * List of "magic numbners" can be found here
 * https://en.wikipedia.org/wiki/List_of_file_signatures 
 */
const fileSignatures: Array<IFileSignature> = [
    {
        extension: 'pdf',
        description: 'PDF document',
        mimeType: 'application/pdf',
        signatures: [
            {
                offset: 0,
                data: [0x25, 0x50, 0x44, 0x46, 0x2d],
            },
        ],
    },
    {
        extension: 'bmp',
        description: 'BMP file, a bitmap format used mostly in the Windows world',
        mimeType: 'image/bmp',
        signatures: [
            {
                offset: 0,
                data: [0x42, 0x4d],
            },
        ],
    },
    {
        extension: 'emf',
        description: 'emf',
        mimeType: 'image/emf',
        signatures: [
            {
                offset: 0,
                data: [0x01, 0x00, 0x00, 0x00],
            },
        ],
    },
    {
        extension: 'gif',
        description: 'Image file encoded in the Graphics Interchange Format (GIF)',
        mimeType: 'image/gif',
        signatures: [
            {
                offset: 0,
                data: [0x47, 0x49, 0x46, 0x38, 0x37, 0x61],
            },
            {
                offset: 0,
                data: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61],
            },
        ],
    },
    {
        extension: 'hdp',
        description: 'HD Photo',
        mimeType: 'image/vnd.ms-photo',
        signatures: [
            {
                offset: 0,
                data: [0x49, 0x49, 0xbc, 0x00],
            },
            {
                offset: 0,
                data: [0x49, 0x49, 0xbc, 0x01],
            },
        ],
    },
    {
        extension: 'jp2',
        description: 'JPEG 2000 format',
        mimeType: 'image/jp2',
        signatures: [
            {
                offset: 0,
                data: [0x00, 0x00, 0x00, 0x0c, 0x6a, 0x50, 0x20, 0x20, 0x0d, 0x0a, 0x87, 0x0a],
            },
            {
                offset: 0,
                data: [0xff, 0x4f, 0xff, 0x51],
            },
        ],
    },
    {
        extension: 'jpg',
        description: 'JPEG raw or in the JFIF or Exif file format',
        mimeType: 'image/jpeg',
        signatures: [
            {
                offset: 0,
                data: [0xff, 0xd8, 0xff, 0xdb],
            },
            {
                offset: 0,
                data: [0xff, 0xd8, 0xff, 0xe0, 0x00, 0x10, 0x4a, 0x46, 0x49, 0x46, 0x00, 0x01],
            },
            {
                offset: 0,
                data: [0xff, 0xd8, 0xff, 0xee],
            },
            {
                offset: 0,
                data: [0xff, 0xd8, 0xff, 0xe1, 0xffff, 0xffff, 0x45, 0x78, 0x69, 0x66, 0x00, 0x00],
            },
        ],
    },
    {
        extension: 'png',
        description: 'Image encoded in the Portable Network Graphics format',
        mimeType: 'image/png',
        signatures: [
            {
                offset: 0,
                data: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
            },
        ],
    },

    {
        extension: 'tif',
        description: 'Tagged Image File Format',
        mimeType: 'image/tif',
        signatures: [
            {
                offset: 0,
                data: [0x49, 0x49, 0x2a, 0x00],
            },
            {
                offset: 0,
                data: [0x4d, 0x4d, 0x00, 0x2a],
            },
        ],
    },
    {
        extension: 'tiff',
        description: 'BigTIFF',
        mimeType: 'image/tiff',
        signatures: [
            {
                offset: 0,
                data: [0x49, 0x49, 0x2b, 0x00],
            },
            {
                offset: 0,
                data: [0x4d, 0x4d, 0x00, 0x2b],
            },
        ],
    },
    {
        extension: 'cr2',
        description: 'Canon\'s RAW format is based on TIFF',
        mimeType: 'image/x-canon-cr2',
        signatures: [
            {
                offset: 0,
                data: [0x49, 0x49, 0x2a, 0x00, 0x10, 0x00, 0x00, 0x00, 0x43, 0x52],
            },
        ],
    },
    {
        extension: 'wmf',
        description: 'Windows Metafile',
        mimeType: 'image/wmf',
        signatures: [
            {
                offset: 0,
                data: [0x9a, 0xc6, 0xcd, 0xd7],
            },
        ],
    },
];

export function signatureArrayLength(): number {
    return Math.max(
        ...fileSignatures.map(signature =>
            Math.max(...signature.signatures.map(array => array.data.length)),
        ),
    );
}

export async function checkSignature(
    data: Blob,
    onIsImageCallback?: () => void,
): Promise<IFileSignature> {
    const arrayData = new Uint8Array(await data.arrayBuffer());
    const result = fileSignatures.map(header =>
        header.signatures
            .map(sig =>
                sig.data
                    .map((d, idx) =>
                        arrayData[sig.offset + idx] > 0xff
                            ? true
                            : arrayData[sig.offset + idx] === d,
                    )
                    .every(v => v),
            )
            .some(v => v),
    );
    const idx = result.indexOf(true);
    if (idx === -1) {
        devError(new Error('Unknown file signature!!!'));
        return fileSignatures[0];
    }

    if (onIsImageCallback && fileSignatures[idx].extension !== 'pdf') {
        onIsImageCallback();
    }

    return fileSignatures[idx];
}

export async function checkExtension(data: Blob, callOnImage?: () => void): Promise<string> {
    const sig = await checkSignature(data, callOnImage);
    return sig.extension;
}
